import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Modal,
  ModalProps,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { differenceInDays, format, startOfDay, subDays } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface TimeOffPolicyApplyChangesModalProps extends ModalProps {
  categoryTitle: string;
  policyTitle: string;
  initialDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  isDateDisabled?: boolean;
  hideDate?: boolean;
  employeesCount: number;
  isSaving: boolean;
  onApply: (dateEffectiveFrom: string | null) => Promise<void>;
}

export function TimeOffPolicyApplyChangesModal({
  categoryTitle,
  policyTitle,
  initialDate,
  minDate,
  maxDate,
  isDateDisabled,
  hideDate,
  employeesCount,
  isSaving,
  onApply,
  ...props
}: TimeOffPolicyApplyChangesModalProps) {
  const { id } = props;
  const { t } = useTranslation();
  const { close } = useModals();
  const { renderDate } = useFormatter();

  const today = startOfDay(new Date());
  const defaultDate =
    initialDate ?? (minDate && minDate.getTime() > today.getTime() ? minDate : today);

  const [date, setDate] = useState<Date>(defaultDate);
  const [confirmed, setConfirmed] = useState(false);

  const isPast = !hideDate && date.getTime() < today.getTime();
  const daysFromToday = differenceInDays(date, today);

  const handleApply = async () => {
    if (isPast && !confirmed) return;
    try {
      await onApply(hideDate ? null : format(date, "yyyy-MM-dd"));
      close(id);
    } catch {
      // keep modal open on error
    }
  };

  return (
    <Modal
      {...props}
      title={
        hideDate
          ? t("time_off:apply_to_current_rules")
          : t("time_off:choose_effective_date_to_apply_changes")
      }
      caption={`${categoryTitle} · ${policyTitle}`}
      withCloser
      actions={
        <Stack gap={"smd"} direction={"vertical"}>
          <Button
            size={"lg"}
            className={"w-full"}
            isLoading={isSaving}
            disabled={isPast && !confirmed}
            onClick={handleApply}
          >
            {hideDate
              ? t("time_off:apply_to_current_rules")
              : t("time_off:apply_changes_from_date", { date: renderDate(date) })}
          </Button>
          <Button size={"lg"} variant={"plain"} className={"w-full"} onClick={() => close(id)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"lg"}>
        {!hideDate &&
          (isDateDisabled ? (
            <Title
              header={t("time_off:effective_date")}
              caption={`${renderDate(date)} · ${t("time_off:current_rules")}`}
            />
          ) : (
            <Datepicker
              label={t("time_off:effective_date")}
              value={date}
              onChange={(d) => d && setDate(d)}
              minDate={minDate ?? today}
              maxDate={maxDate}
            />
          ))}

        <Box className={"bg-dark/5 border-dark/5 border rounded-xs p-5"}>
          <Stack gap={"md"}>
            <Avatar emoji={isPast ? ":warning:" : ":information_source:"} size={"md"} />

            {isPast ? (
              <Stack gap={"md"}>
                <Title
                  header={t("time_off:balances_will_be_recalculated_count", {
                    count: employeesCount,
                  })}
                  size={"sm"}
                />
                <Title
                  header={t("time_off:changes_will_take_effect_on", { date: renderDate(date) })}
                  caption={t("time_off:affected_period", {
                    start: renderDate(date),
                    end: renderDate(subDays(today, 1)),
                  })}
                  size={"sm"}
                />
                <Title
                  header={t("time_off:historical_balances_will_be_recalculated")}
                  caption={t("time_off:some_balances_may_become_negative")}
                  size={"sm"}
                />
                <Stack gap={"xs"}>
                  <Checkbox
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    title={t("time_off:i_understand_historical_changes")}
                  />
                  <Text className={"text-secondary text-sm pl-7"}>
                    {t("time_off:approved_timeoffs_may_exceed_caption")}
                  </Text>
                </Stack>
              </Stack>
            ) : (
              <Stack gap={"md"}>
                <Title
                  header={t("time_off:employees_will_use_these_rules_count", {
                    count: employeesCount,
                  })}
                  size={"sm"}
                />
                {!hideDate && (
                  <Title
                    header={t("time_off:changes_will_take_effect_on", { date: renderDate(date) })}
                    caption={t("time_off:in_n_days", { count: daysFromToday })}
                    size={"sm"}
                  />
                )}
                <Title
                  header={t("time_off:will_affect_only_future_accruals")}
                  caption={t("time_off:existing_balances_will_stay_unchanged")}
                  size={"sm"}
                />
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>
    </Modal>
  );
}
